import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bot,
  Clock3,
  Cpu,
  Database,
  Gauge,
  HeartPulse,
  Layers3,
  MemoryStick,
  RadioTower,
  RefreshCw,
  ShieldAlert,
  Target,
  TimerReset,
  UsersRound,
  Zap
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CHART_COLORS = ['#dbeafe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8', '#0f172a'];

const defaultFilters = {
  windowMinutes: 60,
  inactivityDays: 14,
  ghostingDays: 3,
  limit: 25
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatNumber = (value, digits = 0) => (
  new Intl.NumberFormat('en-US', {
    notation: Math.abs(toNumber(value)) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: digits
  }).format(toNumber(value))
);

const formatMetric = (value, suffix = '', digits = 0) => `${formatNumber(value, digits)}${suffix}`;

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const relativeTime = (value) => {
  if (!value) return 'No timestamp';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No timestamp';

  const diff = Date.now() - date.getTime();
  const absoluteDiff = Math.abs(diff);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const suffix = diff < 0 ? 'from now' : 'ago';

  if (absoluteDiff < minute) return 'Just now';
  if (absoluteDiff < hour) return `${Math.floor(absoluteDiff / minute)}m ${suffix}`;
  if (absoluteDiff < day) return `${Math.floor(absoluteDiff / hour)}h ${suffix}`;
  return `${Math.floor(absoluteDiff / day)}d ${suffix}`;
};

const labelize = (value = '') => String(value || 'Unknown')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bAi\b/g, 'AI')
  .replace(/\bApi\b/g, 'API')
  .replace(/\bCv\b/g, 'CV')
  .replace(/\bDb\b/g, 'DB')
  .replace(/\bHttp\b/g, 'HTTP')
  .replace(/\bIp\b/g, 'IP')
  .replace(/\bKyc\b/g, 'KYC')
  .replace(/\bRbac\b/g, 'RBAC');

const getErrorMessage = (error) => (
  error?.response?.data?.error || error?.message || 'The monitoring request could not be completed.'
);

const getSeverityRank = (status) => ({
  critical: 4,
  overdue: 4,
  warning: 3,
  due_soon: 2,
  operational: 1,
  success: 1
}[String(status || '').toLowerCase()] || 0);

const toneClasses = (tone) => ({
  slate: 'border-slate-200 bg-slate-50 text-slate-700',
  blue: 'border-blue-100 bg-blue-50 text-blue-700',
  emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-100 bg-amber-50 text-amber-700',
  rose: 'border-rose-100 bg-rose-50 text-rose-700'
}[tone] || 'border-slate-200 bg-slate-50 text-slate-700');

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['critical', 'overdue', 'failed', 'error'].includes(normalized)) return 'rose';
  if (['warning', 'due_soon', 'pending'].includes(normalized)) return 'amber';
  if (['operational', 'success', 'active', 'completed', 'healthy'].includes(normalized)) return 'emerald';
  return 'slate';
};

const reference = (value) => value ? String(value).split('-')[0].toUpperCase() : 'N/A';

function StatusPill({ status, children }) {
  const tone = statusTone(status);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClasses(tone)}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone === 'rose' ? 'bg-rose-500' : tone === 'amber' ? 'bg-amber-500' : tone === 'emerald' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      {children || labelize(status)}
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, detail, tone = 'blue' }) {
  return (
    <section className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
          <p className="mt-3 text-2xl font-bold tracking-tight text-slate-950">{value}</p>
          {detail && <p className="mt-1 text-sm leading-5 text-slate-500">{detail}</p>}
        </div>
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${toneClasses(tone)}`}>
          <Icon size={20} />
        </span>
      </div>
    </section>
  );
}

function Panel({ icon: Icon, title, subtitle, children, actions, className = '' }) {
  return (
    <section className={`rounded-[24px] border border-slate-100 bg-white shadow-sm ${className}`}>
      <div className="flex flex-col gap-3 border-b border-slate-100 p-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-100 bg-slate-50 text-slate-900">
              <Icon size={18} />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-slate-950">{title}</h2>
            {subtitle && <p className="mt-1 text-sm leading-5 text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm font-medium text-slate-500">
      {children}
    </div>
  );
}

function ProgressBar({ value, max = 100, tone = 'blue' }) {
  const width = max > 0 ? Math.min(100, Math.max(0, (toNumber(value) / max) * 100)) : 0;
  const color = {
    blue: 'bg-blue-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    slate: 'bg-slate-900'
  }[tone] || 'bg-blue-500';

  return (
    <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
    </div>
  );
}

function MiniStat({ label, value, detail }) {
  return (
    <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-2 break-words text-lg font-bold text-slate-950">{value}</p>
      {detail && <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>}
    </div>
  );
}

function DistributionList({ rows = [], valueKey = 'count', emptyLabel = 'No records available.', showAverage = false }) {
  const cleanRows = asArray(rows);
  const max = Math.max(...cleanRows.map((row) => toNumber(row[valueKey])), 0);

  if (!cleanRows.length) return <EmptyState>{emptyLabel}</EmptyState>;

  return (
    <div className="space-y-4">
      {cleanRows.map((row, index) => {
        const key = row.key || row.label || row.status || row.role || row.band || row.engineVersion || index;
        const count = toNumber(row[valueKey]);
        return (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="truncate font-semibold text-slate-700">{row.label || labelize(key)}</span>
              <span className="shrink-0 font-semibold text-slate-950">{formatNumber(count)}</span>
            </div>
            <ProgressBar value={count} max={max || 1} tone={index % 2 === 0 ? 'blue' : 'slate'} />
            {showAverage && row.averageScore !== null && row.averageScore !== undefined && (
              <p className="text-xs text-slate-400">Average score {formatMetric(row.averageScore, '%', 1)}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BarChartBlock({ rows = [], label = 'Count', emptyLabel, ariaLabel }) {
  const cleanRows = asArray(rows).filter((row) => toNumber(row.value) > 0);

  if (!cleanRows.length) return <EmptyState>{emptyLabel}</EmptyState>;

  const data = {
    labels: cleanRows.map((row) => row.label),
    datasets: [{
      label,
      data: cleanRows.map((row) => toNumber(row.value)),
      backgroundColor: cleanRows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
      borderRadius: 5,
      borderSkipped: false,
      maxBarThickness: 34
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: { color: '#64748b', font: { family: 'inherit' } },
        grid: { display: false, drawBorder: false }
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, color: '#64748b', font: { family: 'inherit' } },
        grid: { color: 'rgba(100, 116, 139, 0.12)', drawBorder: false }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderWidth: 0,
        cornerRadius: 10,
        displayColors: false,
        padding: 12,
        callbacks: {
          label: (context) => `${formatNumber(context.parsed.y)}`
        }
      }
    }
  };

  return <Bar data={data} options={options} role="img" aria-label={ariaLabel} />;
}

function DoughnutBlock({ rows = [], emptyLabel, ariaLabel }) {
  const cleanRows = asArray(rows).filter((row) => toNumber(row.value) > 0);

  if (!cleanRows.length) return <EmptyState>{emptyLabel}</EmptyState>;

  const total = cleanRows.reduce((sum, row) => sum + toNumber(row.value), 0);
  const data = {
    labels: cleanRows.map((row) => row.label),
    datasets: [{
      data: cleanRows.map((row) => toNumber(row.value)),
      backgroundColor: cleanRows.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
      borderWidth: 0,
      cutout: '72%'
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        cornerRadius: 10,
        displayColors: false,
        padding: 12,
        callbacks: {
          label: (context) => `${context.label}: ${formatNumber(context.parsed)}`
        }
      }
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-[190px] flex-1">
        <Doughnut data={data} options={options} role="img" aria-label={ariaLabel} />
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-950">{formatNumber(total)}</span>
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">Total</span>
        </div>
      </div>
      <div className="mt-4 grid gap-2">
        {cleanRows.slice(0, 5).map((row, index) => (
          <div key={row.label} className="flex items-center justify-between gap-3 text-xs">
            <span className="flex min-w-0 items-center gap-2 text-slate-500">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
              <span className="truncate">{row.label}</span>
            </span>
            <span className="font-semibold text-slate-900">{formatNumber(row.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MonitoringSkeleton() {
  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <SkeletonBlock className="h-24 rounded-[24px]" />
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => <SkeletonBlock key={index} className="h-32 rounded-[24px]" />)}
        </div>
        <div className="grid gap-6 xl:grid-cols-3">
          <SkeletonBlock className="h-96 rounded-[24px] xl:col-span-2" />
          <SkeletonBlock className="h-96 rounded-[24px]" />
        </div>
        <div className="grid gap-6 xl:grid-cols-2">
          <SkeletonBlock className="h-96 rounded-[24px]" />
          <SkeletonBlock className="h-96 rounded-[24px]" />
        </div>
      </div>
    </div>
  );
}

function Filters({ filters, onChange, updatedAt, isFetching, onRefresh }) {
  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_0.8fr_auto]">
        <label className="grid gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit window</span>
          <select
            value={filters.windowMinutes}
            onChange={(event) => onChange({ ...filters, windowMinutes: Number(event.target.value) })}
            className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-50"
          >
            <option value={15}>15 minutes</option>
            <option value={60}>60 minutes</option>
            <option value={240}>4 hours</option>
            <option value={1440}>24 hours</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Inactive after</span>
          <select
            value={filters.inactivityDays}
            onChange={(event) => onChange({ ...filters, inactivityDays: Number(event.target.value) })}
            className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-50"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Due horizon</span>
          <select
            value={filters.ghostingDays}
            onChange={(event) => onChange({ ...filters, ghostingDays: Number(event.target.value) })}
            className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-50"
          >
            <option value={1}>1 day</option>
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
          </select>
        </label>
        <label className="grid gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rows</span>
          <select
            value={filters.limit}
            onChange={(event) => onChange({ ...filters, limit: Number(event.target.value) })}
            className="h-10 rounded-[16px] border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 focus:ring-4 focus:ring-blue-50"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <div className="flex flex-col justify-end gap-1.5">
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-[16px] bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <p className="text-center text-[11px] font-medium text-slate-400 sm:text-right">Updated {relativeTime(updatedAt)}</p>
        </div>
      </div>
    </div>
  );
}

function HealthCheckList({ checks }) {
  const rows = asArray(checks)
    .slice()
    .sort((first, second) => getSeverityRank(second.status) - getSeverityRank(first.status));

  if (!rows.length) return <EmptyState>No health checks were returned.</EmptyState>;

  return (
    <div className="space-y-3">
      {rows.map((check) => (
        <div key={check.key} className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-slate-950">{check.label}</p>
              <p className="mt-1 text-sm leading-5 text-slate-500">{check.detail}</p>
            </div>
            <StatusPill status={check.status} />
          </div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{check.metric || 'No metric'}</p>
        </div>
      ))}
    </div>
  );
}

function RiskTable({ risks }) {
  const rows = asArray(risks);
  if (!rows.length) return <EmptyState>No overdue or near-due workflow items were found.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[1080px] divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Status</th>
            <th className="px-6 py-4">Workflow</th>
            <th className="px-6 py-4">Job</th>
            <th className="px-6 py-4">Candidate</th>
            <th className="px-6 py-4">Employer</th>
            <th className="px-6 py-4">Due</th>
            <th className="px-6 py-4">Match</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((risk) => (
            <tr key={risk.id} className="align-top hover:bg-slate-50">
              <td className="px-6 py-4"><StatusPill status={risk.severity} /></td>
              <td className="px-6 py-4">
                <p className="font-semibold text-slate-950">{risk.label}</p>
                <p className="mt-1 text-xs text-slate-500">{labelize(risk.reason)} assigned to {labelize(risk.ownerType)}</p>
              </td>
              <td className="px-6 py-4 text-slate-600">{risk.job?.title || 'Not recorded'}</td>
              <td className="px-6 py-4 text-slate-600">{risk.candidate?.name || 'Not recorded'}</td>
              <td className="px-6 py-4 text-slate-600">{risk.employer?.companyName || 'Not recorded'}</td>
              <td className="px-6 py-4 text-slate-600">{formatDateTime(risk.dueAt)}</td>
              <td className="px-6 py-4 font-mono text-xs text-slate-600">Ref {reference(risk.matchId)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SlowEndpointsTable({ endpoints }) {
  const rows = asArray(endpoints);
  if (!rows.length) return <EmptyState>No endpoint timing data is available for this window.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[860px] divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Endpoint</th>
            <th className="px-6 py-4">Requests</th>
            <th className="px-6 py-4">P95</th>
            <th className="px-6 py-4">Average</th>
            <th className="px-6 py-4">5xx</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((endpoint) => (
            <tr key={`${endpoint.method}:${endpoint.path}`} className="align-top hover:bg-slate-50">
              <td className="px-6 py-4">
                <span className="mr-2 inline-flex rounded-md bg-slate-100 px-2 py-1 font-mono text-xs font-bold text-slate-600">{endpoint.method}</span>
                <span className="break-all font-mono text-xs text-slate-700">{endpoint.path}</span>
              </td>
              <td className="px-6 py-4 text-slate-600">{formatNumber(endpoint.requests)}</td>
              <td className="px-6 py-4 font-semibold text-slate-950">{formatMetric(endpoint.p95DurationMs, ' ms')}</td>
              <td className="px-6 py-4 text-slate-600">{formatMetric(endpoint.averageDurationMs, ' ms')}</td>
              <td className="px-6 py-4 text-slate-600">{formatNumber(endpoint.serverErrors)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AiOperationsTable({ operations }) {
  const rows = asArray(operations);
  if (!rows.length) return <EmptyState>No AI operations were recorded for this window.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[920px] divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Operation</th>
            <th className="px-6 py-4">Provider</th>
            <th className="px-6 py-4">Calls</th>
            <th className="px-6 py-4">Failed</th>
            <th className="px-6 py-4">Tokens</th>
            <th className="px-6 py-4">Avg latency</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((operation) => (
            <tr key={`${operation.operation}:${operation.provider}:${operation.model}`} className="align-top hover:bg-slate-50">
              <td className="px-6 py-4">
                <p className="font-semibold text-slate-950">{operation.label || labelize(operation.operation)}</p>
                <p className="mt-1 font-mono text-xs text-slate-400">{operation.model || 'Model not recorded'}</p>
              </td>
              <td className="px-6 py-4 text-slate-600">{labelize(operation.provider)}</td>
              <td className="px-6 py-4 text-slate-600">{formatNumber(operation.calls)}</td>
              <td className="px-6 py-4 text-slate-600">{formatNumber(operation.failedCalls)}</td>
              <td className="px-6 py-4 text-slate-600">{formatNumber(operation.totalTokens)}</td>
              <td className="px-6 py-4 text-slate-600">{formatMetric(operation.averageLatencyMs, ' ms')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatchesTable({ matches }) {
  const rows = asArray(matches);
  if (!rows.length) return <EmptyState>No recent match movement was recorded.</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[1080px] divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Match</th>
            <th className="px-6 py-4">Job</th>
            <th className="px-6 py-4">Candidate</th>
            <th className="px-6 py-4">Employer</th>
            <th className="px-6 py-4">Score</th>
            <th className="px-6 py-4">Lifecycle</th>
            <th className="px-6 py-4">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((match) => (
            <tr key={match.id} className="align-top hover:bg-slate-50">
              <td className="px-6 py-4 font-mono text-xs text-slate-600">Ref {reference(match.id)}</td>
              <td className="px-6 py-4">
                <p className="max-w-[280px] truncate font-semibold text-slate-950">{match.job?.title || 'Untitled job'}</p>
                <p className="mt-1 text-xs text-slate-500">{labelize(match.job?.status)}</p>
              </td>
              <td className="px-6 py-4 text-slate-600">{match.candidate?.name || 'Not recorded'}</td>
              <td className="px-6 py-4 text-slate-600">{match.employer?.companyName || 'Not recorded'}</td>
              <td className="px-6 py-4">
                <p className="font-semibold text-slate-950">{formatMetric(match.score?.overall, '%', 1)}</p>
                <p className="mt-1 text-xs text-slate-500">Band {match.score?.band || 'Unknown'}</p>
              </td>
              <td className="px-6 py-4"><StatusPill status={match.lifecycle?.nullifiedAt ? 'critical' : match.lifecycle?.status} /></td>
              <td className="px-6 py-4 text-slate-600" title={formatDateTime(match.lastUpdatedAt)}>{relativeTime(match.lastUpdatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityStream({ events }) {
  const rows = asArray(events);
  if (!rows.length) return <EmptyState>No audit events have been recorded in this window.</EmptyState>;

  return (
    <div className="max-h-[400px] overflow-y-auto pr-3 space-y-3">
      {rows.slice(0, 25).map((event) => (
        <div key={event.id} className="flex items-start gap-3 rounded-[16px] border border-slate-100 bg-slate-50 p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-white text-blue-600">
            <Activity size={17} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-950">{event.label || labelize(event.action)}</p>
                <p className="mt-1 truncate text-sm text-slate-500">{event.actor?.name || event.actor?.email || 'System'} on {labelize(event.resourceType || 'system')}</p>
              </div>
              <p className="shrink-0 text-xs font-semibold text-slate-400" title={formatDateTime(event.createdAt)}>{relativeTime(event.createdAt)}</p>
            </div>
            <p className="mt-2 break-all font-mono text-xs text-slate-400">{event.request?.ipAddress || 'IP not recorded'}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function UserTable({ users, emptyLabel }) {
  const rows = asArray(users);
  if (!rows.length) return <EmptyState>{emptyLabel}</EmptyState>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[720px] divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50/50 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <tr>
            <th className="px-6 py-4">Account</th>
            <th className="px-6 py-4">Role</th>
            <th className="px-6 py-4">Location</th>
            <th className="px-6 py-4">Last login</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((user) => (
            <tr key={user.userId} className="align-top hover:bg-slate-50">
              <td className="px-6 py-4">
                <p className="max-w-[240px] truncate font-semibold text-slate-950">{user.companyName || user.name || user.email}</p>
                <p className="mt-1 max-w-[240px] truncate text-xs text-slate-500">{user.email}</p>
              </td>
              <td className="px-6 py-4 text-slate-600">{labelize(user.role)}</td>
              <td className="px-6 py-4 text-slate-600">{user.location || 'Not recorded'}</td>
              <td className="px-6 py-4 text-slate-600" title={formatDateTime(user.lastLoginAt)}>{relativeTime(user.lastLoginAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonitoringPage() {
  const [filters, setFilters] = useState(defaultFilters);

  const queryKey = useMemo(() => ['admin-monitoring', filters], [filters]);
  const {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch
  } = useQuery(queryKey, () => adminAPI.getMonitoring(filters), {
    keepPreviousData: true,
    staleTime: 15000,
    refetchInterval: 30000,
    refetchOnWindowFocus: true
  });

  if (isLoading && !data) return <MonitoringSkeleton />;

  if (isError && !data) {
    return (
      <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-xl rounded-[24px] border border-rose-100 bg-white p-6 text-center shadow-sm">
          <AlertCircle className="mx-auto h-11 w-11 text-rose-500" />
          <h1 className="mt-4 text-lg font-semibold text-slate-950">Monitoring data is unavailable</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">{getErrorMessage(error)}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-6 inline-flex items-center justify-center rounded-[16px] bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const healthSummary = data?.systemHealth?.summary || {};
  const checks = asArray(healthSummary.checks);
  const nonOperationalChecks = checks.filter((check) => String(check.status).toLowerCase() !== 'operational');
  const db = data?.systemHealth?.database || {};
  const runtime = data?.systemHealth?.runtime || {};
  const cvQueue = data?.systemHealth?.cvQueue || {};
  const kycQueue = data?.systemHealth?.kycQueue || {};
  const queueConfig = data?.systemHealth?.queueConfig || {};
  const activeUsers = data?.activeUsers || {};
  const matching = data?.matching || {};
  const performance = data?.performance || {};
  const inactivity = data?.inactivity || {};
  const api = performance.api || {};
  const ai = performance.ai || {};
  const email = performance.email || {};
  const matchSummary = matching.summary || {};
  const activitySummary = activeUsers.summary || {};
  const riskCount = asArray(inactivity.eventRisks).length;
  const apiErrorCount = toNumber(api.clientErrorsWindow) + toNumber(api.serverErrorsWindow);
  const dbConnectionUsage = toNumber(db.connections?.usagePct);
  const hostMemory = toNumber(runtime.host?.memoryUsagePct);
  const heapMemory = toNumber(runtime.processMemory?.heapUsagePct);

  const apiChartRows = asArray(api.statusBuckets).map((row) => ({ label: row.label, value: row.count }));
  const roleChartRows = asArray(activeUsers.roleBreakdown).map((row) => ({ label: row.label, value: row.active }));
  const matchStatusRows = asArray(matching.statusBreakdown).map((row) => ({ label: row.label, value: row.count }));
  const emailRows = asArray(email.statusBreakdown).map((row) => ({ label: row.label, value: row.count }));
  const largestTableMax = Math.max(...asArray(db.largestTables).map((table) => toNumber(table.totalMb)), 0);

  return (
    <div className="min-h-full bg-[#f8f9fa] p-4 pt-20 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <PageHeader
          eyebrow="System"
          title="System Monitoring"
          description="Operational oversight across uptime, API performance, AI usage, queues, matching, activity, and workflow risk."
          actions={<StatusPill status={healthSummary.status}>{labelize(healthSummary.status || 'unknown')}</StatusPill>}
        />

        <Filters
          filters={filters}
          onChange={setFilters}
          updatedAt={data?.generatedAt}
          isFetching={isFetching}
          onRefresh={() => refetch()}
        />

        {isError && data && (
          <div className="rounded-[24px] border border-amber-100 bg-amber-50 p-6 text-sm font-medium text-amber-800">
            The latest refresh failed. Showing the last successful snapshot. {getErrorMessage(error)}
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon={HeartPulse}
            label="Platform health"
            value={labelize(healthSummary.status)}
            detail={`${formatNumber(healthSummary.criticalCount)} critical, ${formatNumber(healthSummary.warningCount)} warnings`}
            tone={statusTone(healthSummary.status)}
          />
          <MetricCard
            icon={Gauge}
            label="API P95"
            value={formatMetric(api.p95DurationMs, ' ms')}
            detail={`${formatNumber(api.requestsWindow)} audited requests, ${formatNumber(apiErrorCount)} errors`}
            tone={toNumber(api.p95DurationMs) >= 1500 || apiErrorCount ? 'amber' : 'emerald'}
          />
          <MetricCard
            icon={Database}
            label="DB connections"
            value={formatMetric(dbConnectionUsage, '%', 1)}
            detail={`${formatNumber(db.connections?.total)} of ${formatNumber(db.connections?.max)} connections in use`}
            tone={dbConnectionUsage >= 70 ? 'amber' : 'blue'}
          />
          <MetricCard
            icon={MemoryStick}
            label="Host memory"
            value={formatMetric(hostMemory, '%', 1)}
            detail={`${formatMetric(heapMemory, '%', 1)} process heap, ${formatMetric(runtime.processMemory?.rssMb, ' MB', 1)} RSS`}
            tone={hostMemory >= 85 || heapMemory >= 85 ? 'amber' : 'blue'}
          />
          <MetricCard
            icon={UsersRound}
            label="Active users"
            value={formatNumber(activitySummary.activeUsersWindow)}
            detail={`${formatNumber(activitySummary.activityEventsWindow)} audit events in the selected window`}
            tone="blue"
          />
          <MetricCard
            icon={Target}
            label="Match movement"
            value={formatNumber(matchSummary.matchesWindow)}
            detail={`${formatNumber(matchSummary.updatedWindow)} updated, ${formatNumber(matchSummary.awaitingCandidateResponse)} awaiting candidate`}
            tone="blue"
          />
          <MetricCard
            icon={Zap}
            label="AI usage"
            value={formatNumber(ai.totalTokensWindow)}
            detail={`${formatNumber(ai.callsWindow)} calls, ${formatNumber(ai.failedCallsWindow)} failed`}
            tone={toNumber(ai.failedCallsWindow) ? 'amber' : 'blue'}
          />
          <MetricCard
            icon={ShieldAlert}
            label="Workflow risk"
            value={formatNumber(riskCount)}
            detail={`${formatNumber(inactivity.summary?.overdueCandidateResponses)} overdue candidate responses`}
            tone={riskCount ? 'amber' : 'emerald'}
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
          <Panel
            icon={AlertTriangle}
            title="Needs Attention"
            subtitle="Items that require operational review before they become user-facing issues."
          >
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-950">Health signals</h3>
                  <StatusPill status={nonOperationalChecks.length ? 'warning' : 'operational'}>
                    {nonOperationalChecks.length ? `${nonOperationalChecks.length} to review` : 'Clear'}
                  </StatusPill>
                </div>
                <HealthCheckList checks={nonOperationalChecks.length ? nonOperationalChecks : checks.slice(0, 4)} />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-950">Workflow due queue</h3>
                  <span className="text-xs font-semibold text-slate-400">{formatNumber(riskCount)} items</span>
                </div>
                <RiskTable risks={asArray(inactivity.eventRisks).slice(0, 6)} />
              </div>
            </div>
          </Panel>

          <Panel
            icon={RadioTower}
            title="Live Audit Stream"
            subtitle="Recent recorded platform actions. These are audit events, not raw API traffic."
          >
            <ActivityStream events={activeUsers.liveEvents} />
          </Panel>
        </div>

        <div className="grid gap-6 xl:grid-cols-3">
          <Panel icon={Cpu} title="Runtime" subtitle="API process and host capacity in the current server process.">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
              <MiniStat label="Node" value={runtime.node?.version || 'Unknown'} detail={`${labelize(runtime.node?.env)} environment, PID ${runtime.node?.pid || 'N/A'}`} />
              <MiniStat label="Process uptime" value={`${formatNumber((toNumber(runtime.node?.uptimeSeconds) / 3600), 1)}h`} detail={`Started ${formatDateTime(runtime.node?.startedAt)}`} />
              <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Process heap</p>
                  <p className="text-sm font-bold text-slate-950">{formatMetric(heapMemory, '%', 1)}</p>
                </div>
                <div className="mt-3"><ProgressBar value={heapMemory} tone={heapMemory >= 85 ? 'amber' : 'blue'} /></div>
                <p className="mt-2 text-xs text-slate-500">{formatMetric(runtime.processMemory?.heapUsedMb, ' MB', 1)} used of {formatMetric(runtime.processMemory?.heapTotalMb, ' MB', 1)}</p>
              </div>
              <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Host memory</p>
                  <p className="text-sm font-bold text-slate-950">{formatMetric(hostMemory, '%', 1)}</p>
                </div>
                <div className="mt-3"><ProgressBar value={hostMemory} tone={hostMemory >= 85 ? 'amber' : 'blue'} /></div>
                <p className="mt-2 text-xs text-slate-500">{formatMetric(runtime.host?.usedMemoryMb, ' MB', 0)} used of {formatMetric(runtime.host?.totalMemoryMb, ' MB', 0)}</p>
              </div>
            </div>
          </Panel>

          <Panel icon={Database} title="Database" subtitle="Connection pressure, uptime, database size, and largest relations.">
            <div className="grid gap-4">
              <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Connection usage</p>
                  <p className="text-sm font-bold text-slate-950">{formatMetric(dbConnectionUsage, '%', 1)}</p>
                </div>
                <div className="mt-3"><ProgressBar value={dbConnectionUsage} tone={dbConnectionUsage >= 70 ? 'amber' : 'blue'} /></div>
                <p className="mt-2 text-xs text-slate-500">{formatNumber(db.connections?.active)} active, {formatNumber(db.connections?.idle)} idle, {formatNumber(db.connections?.waiting)} waiting on resources</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <MiniStat label="DB uptime" value={`${formatNumber((toNumber(db.uptimeSeconds) / 3600), 1)}h`} detail={formatDateTime(db.time)} />
                <MiniStat label="DB size" value={formatMetric(db.size?.mb, ' MB', 1)} detail={db.size?.name || 'Current database'} />
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-950">Largest tables</h3>
                {asArray(db.largestTables).length ? asArray(db.largestTables).slice(0, 6).map((table) => (
                  <div key={`${table.schema}.${table.table}`} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="truncate font-mono text-slate-600">{table.table}</span>
                      <span className="font-semibold text-slate-950">{formatMetric(table.totalMb, ' MB', 1)}</span>
                    </div>
                    <ProgressBar value={table.totalMb} max={largestTableMax || 1} tone="slate" />
                  </div>
                )) : <EmptyState>No table-size data is available.</EmptyState>}
              </div>
            </div>
          </Panel>

          <Panel icon={Layers3} title="Queues" subtitle="Queue backend, delivery queues, KYC backlog, and CV processing state.">
            <div className="grid gap-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                <MiniStat label="Backend" value={labelize(queueConfig.backend)} detail={queueConfig.enabled ? 'BullMQ enabled' : 'Queue backend disabled'} />
                <MiniStat label="Redis" value={queueConfig.redis?.host || 'Configured'} detail={queueConfig.redis?.db !== undefined ? `DB ${queueConfig.redis.db}` : 'Connection summary'} />
                <MiniStat label="CV pending" value={formatNumber(cvQueue.pendingJobs)} detail={`${formatNumber(cvQueue.processingJobs)} processing, ${formatNumber(cvQueue.staleProcessing)} stale`} />
                <MiniStat label="KYC open" value={formatNumber(kycQueue.openQueue)} detail={`${formatMetric(kycQueue.oldestOpenHours, 'h', 1)} oldest open`} />
              </div>
              <div className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Configured queues</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {Object.entries(queueConfig.queues || {}).map(([key, value]) => (
                    <span key={key} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Panel>
        </div>

        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <Panel icon={Gauge} title="API Performance" subtitle="Authenticated API audit events grouped by status and endpoint latency.">
            <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
              <div className="min-h-[260px]">
                <DoughnutBlock rows={apiChartRows} emptyLabel="No API audit data in this window." ariaLabel="API status distribution" />
              </div>
              <SlowEndpointsTable endpoints={api.slowEndpoints} />
            </div>
          </Panel>

          <Panel icon={Bot} title="AI and Messaging" subtitle="AI operation reliability, token use, latency, and outbound email delivery pressure.">
            <div className="grid gap-5 lg:grid-cols-[1.25fr_0.75fr]">
              <AiOperationsTable operations={ai.operations} />
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  <MiniStat label="AI p95 latency" value={formatMetric(ai.p95LatencyMs, ' ms')} detail={`${formatMetric(ai.averageLatencyMs, ' ms')} average`} />
                  <MiniStat label="Email due" value={formatNumber(email.duePending)} detail={`${formatNumber(email.pendingJobs)} pending, ${formatNumber(email.failedWindow)} failed in window`} />
                </div>
                <DistributionList rows={emailRows.map((row) => ({ label: row.label, count: row.value }))} emptyLabel="No email queue records." />
              </div>
            </div>
          </Panel>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <Panel icon={Target} title="Matching Engine Oversight" subtitle="Match volume, score distribution, engine versions, stage-three activity, and recent match movement.">
            <div className="grid gap-5 lg:grid-cols-3">
              <MiniStat label="Average score" value={formatMetric(matchSummary.averageMatchScore, '%', 1)} detail={`${formatMetric(matchSummary.averageMatchScore24h, '%', 1)} in the last 24h`} />
              <MiniStat label="Awaiting candidate" value={formatNumber(matchSummary.awaitingCandidateResponse)} detail={`${formatNumber(matchSummary.overdueCandidateResponse)} overdue responses`} />
              <MiniStat label="Overdue employer next steps" value={formatNumber(matchSummary.overdueNextStep)} detail={`${formatNumber(matchSummary.nullifiedMatches)} nullified matches`} />
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <div className="min-h-[250px]">
                <DoughnutBlock rows={matchStatusRows} emptyLabel="No match statuses found." ariaLabel="Match status distribution" />
              </div>
              <div className="space-y-4">
                <DistributionList rows={matching.scoreDistribution} emptyLabel="No score bands recorded." showAverage />
              </div>
            </div>
            <div className="mt-5">
              <MatchesTable matches={matching.liveMatches} />
            </div>
          </Panel>

          <Panel icon={Zap} title="Engine Versions" subtitle="Seven-day engine output, low confidence signals, and stage-three re-ranking runs.">
            <div className="space-y-4">
              {asArray(matching.engineBreakdown).length ? asArray(matching.engineBreakdown).map((engine) => (
                <div key={engine.engineVersion || engine.label} className="rounded-[16px] border border-slate-100 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-950">{engine.engineVersion || 'Unknown engine'}</p>
                      <p className="mt-1 text-xs text-slate-500">{formatNumber(engine.count)} matches, {formatNumber(engine.stage3Runs)} stage-three runs</p>
                    </div>
                    <span className="text-sm font-bold text-slate-950">{formatMetric(engine.averageScore, '%', 1)}</span>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-500">
                    <div className="flex justify-between"><span>Hybrid</span><span className="font-semibold text-slate-700">{formatMetric(engine.averageHybridScore, '%', 1)}</span></div>
                    <div className="flex justify-between"><span>Deterministic</span><span className="font-semibold text-slate-700">{formatMetric(engine.averageDeterministicScore, '%', 1)}</span></div>
                    <div className="flex justify-between"><span>Low confidence</span><span className="font-semibold text-slate-700">{formatNumber(engine.lowConfidence)}</span></div>
                  </div>
                </div>
              )) : <EmptyState>No engine breakdown is available.</EmptyState>}
            </div>
          </Panel>
        </div>

        <div className="grid gap-6 xl:grid-cols-3">
          <Panel icon={UsersRound} title="People Activity" subtitle="Account activity, login recency, and role-level movement.">
            <div className="grid gap-4 sm:grid-cols-2">
              <MiniStat label="Logged in 15m" value={formatNumber(activitySummary.loggedIn15m)} detail={`${formatNumber(activitySummary.loggedIn60m)} in the last hour`} />
              <MiniStat label="Logged in 24h" value={formatNumber(activitySummary.loggedIn24h)} detail={`${formatNumber(activitySummary.neverLoggedIn)} never logged in`} />
            </div>
            <div className="mt-5 h-64">
              <BarChartBlock rows={roleChartRows} emptyLabel="No active users by role." ariaLabel="Active users by role" />
            </div>
            <div className="mt-5">
              <DistributionList rows={activeUsers.activityByRole} valueKey="events" emptyLabel="No role audit events." />
            </div>
          </Panel>

          <Panel icon={Clock3} title="Recent Logins" subtitle="Most recent admin, employer, and candidate login activity.">
            <UserTable users={activeUsers.recentLogins} emptyLabel="No recent login records were found." />
          </Panel>

          <Panel icon={TimerReset} title="Inactivity Watch" subtitle="Accounts that may need retention, support, or commercial follow-up.">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              <MiniStat label="Inactive candidates" value={formatNumber(inactivity.summary?.inactiveCandidates)} detail={`${filters.inactivityDays}+ days without login`} />
              <MiniStat label="Inactive employers" value={formatNumber(inactivity.summary?.inactiveEmployers)} detail={`${filters.inactivityDays}+ days without login`} />
              <MiniStat label="Unconfirmed interviews" value={formatNumber(inactivity.summary?.unconfirmedInterviews)} detail="Needs scheduling follow-up" />
              <MiniStat label="Overdue offers" value={formatNumber(inactivity.summary?.overdueOfferResponses)} detail="Candidate response pending" />
            </div>
            <div className="mt-5 space-y-4">
              <h3 className="text-sm font-semibold text-slate-950">Candidates to review</h3>
              <UserTable users={asArray(inactivity.inactiveCandidates).slice(0, 5)} emptyLabel="No inactive candidates match the threshold." />
            </div>
          </Panel>
        </div>

        {asArray(data?.degradedSections).length > 0 && (
          <Panel icon={AlertCircle} title="Degraded Data Sections" subtitle="Optional monitoring sections that could not be loaded because a relation or column is not available.">
            <div className="grid gap-3 md:grid-cols-2">
              {asArray(data.degradedSections).map((warning) => (
                <div key={`${warning.section}:${warning.reason}`} className="rounded-[24px] border border-amber-100 bg-amber-50 p-6 text-sm leading-6 text-amber-800">
                  <span className="font-semibold">{labelize(warning.section)}:</span> {warning.reason}
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}

export default MonitoringPage;
